import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TeamMemberCard from './components/TeamMemberCard';
import TeamMemberTable from './components/TeamMemberTable';
import InviteTeamMember from './components/InviteTeamMember';
import TeamStats from './components/TeamStats';
import RoleEditModal from './components/RoleEditModal';

const TeamManagement = () => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedMember, setSelectedMember] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock team members data
  const [teamMembers, setTeamMembers] = useState([
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah@jewelthread.com",
      role: "Owner",
      status: "Active",
      permissions: {
        createProjects: true,
        viewAllProjects: true,
        manageTeam: true,
        accessVault: true,
        clientCommunication: true
      },
      lastActive: "2 minutes ago",
      joinedDate: "2023-01-15"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael@jewelthread.com",
      role: "Designer",
      status: "Active",
      permissions: {
        createProjects: true,
        viewAllProjects: false,
        manageTeam: false,
        accessVault: true,
        clientCommunication: false
      },
      lastActive: "1 hour ago",
      joinedDate: "2023-03-22"
    },
    {
      id: 3,
      name: "Emma Rodriguez",
      email: "emma@jewelthread.com",
      role: "Sales Rep",
      status: "Active",
      permissions: {
        createProjects: false,
        viewAllProjects: true,
        manageTeam: false,
        accessVault: false,
        clientCommunication: true
      },
      lastActive: "3 hours ago",
      joinedDate: "2023-05-10"
    },
    {
      id: 4,
      name: "David Park",
      email: "david@jewelthread.com",
      role: "Manager",
      status: "Active",
      permissions: {
        createProjects: true,
        viewAllProjects: true,
        manageTeam: true,
        accessVault: true,
        clientCommunication: true
      },
      lastActive: "Yesterday",
      joinedDate: "2023-02-28"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      email: "lisa@jewelthread.com",
      role: "Designer",
      status: "Pending",
      permissions: {
        createProjects: true,
        viewAllProjects: false,
        manageTeam: false,
        accessVault: true,
        clientCommunication: false
      },
      lastActive: "Never",
      joinedDate: "2024-01-05"
    }
  ]);

  // Filter team members based on search and filters
  const filteredMembers = teamMembers?.filter(member => {
    const matchesSearch = member?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || member?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || member?.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleInviteMember = async (inviteData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newMember = {
      id: Date.now(),
      name: inviteData?.email?.split('@')?.[0],
      email: inviteData?.email,
      role: inviteData?.role,
      status: "Pending",
      permissions: getDefaultPermissions(inviteData?.role),
      lastActive: "Never",
      joinedDate: new Date()?.toISOString()?.split('T')?.[0]
    };
    
    setTeamMembers(prev => [...prev, newMember]);
  };

  const getDefaultPermissions = (role) => {
    const defaults = {
      Designer: {
        createProjects: true,
        viewAllProjects: false,
        manageTeam: false,
        accessVault: true,
        clientCommunication: false
      },
      'Sales Rep': {
        createProjects: false,
        viewAllProjects: true,
        manageTeam: false,
        accessVault: false,
        clientCommunication: true
      },
      Manager: {
        createProjects: true,
        viewAllProjects: true,
        manageTeam: true,
        accessVault: true,
        clientCommunication: true
      }
    };
    return defaults?.[role] || {};
  };

  const handleUpdateRole = (memberId) => {
    const member = teamMembers?.find(m => m?.id === memberId);
    setSelectedMember(member);
    setIsEditModalOpen(true);
  };

  const handleUpdatePermissions = (memberId, newPermissions) => {
    setTeamMembers(prev => prev?.map(member =>
      member?.id === memberId
        ? { ...member, permissions: newPermissions }
        : member
    ));
  };

  const handleRemoveMember = (memberId) => {
    if (window.confirm('Are you sure you want to remove this team member?')) {
      setTeamMembers(prev => prev?.filter(member => member?.id !== memberId));
    }
  };

  const handleBulkAction = (action, memberIds) => {
    switch (action) {
      case 'activate':
        setTeamMembers(prev => prev?.map(member =>
          memberIds?.includes(member?.id)
            ? { ...member, status: 'Active' }
            : member
        ));
        break;
      case 'deactivate':
        setTeamMembers(prev => prev?.map(member =>
          memberIds?.includes(member?.id)
            ? { ...member, status: 'Inactive' }
            : member
        ));
        break;
      case 'remove':
        if (window.confirm(`Are you sure you want to remove ${memberIds?.length} team member(s)?`)) {
          setTeamMembers(prev => prev?.filter(member => !memberIds?.includes(member?.id)));
        }
        break;
    }
  };

  const handleSaveMemberChanges = async (memberId, newRole, newPermissions) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setTeamMembers(prev => prev?.map(member =>
      member?.id === memberId
        ? { ...member, role: newRole, permissions: newPermissions }
        : member
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Team Management - JewelThread</title>
        <meta name="description" content="Manage your jewelry design team members and their permissions" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                  <Link to="/dashboard" className="hover:text-accent transition-luxury">Dashboard</Link>
                  <Icon name="ChevronRight" size={16} />
                  <span>Team Management</span>
                </div>
                <h1 className="text-3xl font-heading text-foreground">Team Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage your jewelry design team members and their permissions
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                  iconName={viewMode === 'table' ? 'Grid3X3' : 'List'}
                >
                  {viewMode === 'table' ? 'Card View' : 'Table View'}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Team Stats */}
          <TeamStats members={teamMembers} />

          {/* Invite Team Member */}
          <div className="mb-8">
            <InviteTeamMember onInviteMember={handleInviteMember} />
          </div>

          {/* Filters and Search */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6 elevation-1">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search team members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-luxury"
                  />
                </div>
                
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e?.target?.value)}
                  className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-luxury"
                >
                  <option value="all">All Roles</option>
                  <option value="Owner">Owner</option>
                  <option value="Designer">Designer</option>
                  <option value="Sales Rep">Sales Rep</option>
                  <option value="Manager">Manager</option>
                </select>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-luxury"
                >
                  <option value="all">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              
              <div className="text-sm text-muted-foreground">
                Showing {filteredMembers?.length} of {teamMembers?.length} members
              </div>
            </div>
          </div>

          {/* Team Members Display */}
          {viewMode === 'table' ? (
            <TeamMemberTable
              members={filteredMembers}
              onUpdateRole={handleUpdateRole}
              onUpdatePermissions={handleUpdatePermissions}
              onRemoveMember={handleRemoveMember}
              onBulkAction={handleBulkAction}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMembers?.map(member => (
                <TeamMemberCard
                  key={member?.id}
                  member={member}
                  onUpdateRole={handleUpdateRole}
                  onUpdatePermissions={handleUpdatePermissions}
                  onRemoveMember={handleRemoveMember}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {filteredMembers?.length === 0 && (
            <div className="bg-card border border-border rounded-lg p-12 text-center elevation-1">
              <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-lg font-heading text-foreground mb-2">No team members found</h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || filterRole !== 'all' || filterStatus !== 'all' ?'Try adjusting your search or filters' :'Start by inviting your first team member'}
              </p>
              {(!searchTerm && filterRole === 'all' && filterStatus === 'all') && (
                <Button
                  onClick={() => document.querySelector('input[type="email"]')?.focus()}
                  iconName="UserPlus"
                >
                  Invite Team Member
                </Button>
              )}
            </div>
          )}
        </div>
        {/* Role Edit Modal */}
        <RoleEditModal
          member={selectedMember}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedMember(null);
          }}
          onSave={handleSaveMemberChanges}
        />
      </main>
    </div>
  );
};

export default TeamManagement;